import useToast from "@/contexts/toast-context";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { WorkflowMessages } from "@/messages/workflow/workflow.messages";
import { WorkflowTypes } from "@/types/workflow.types";

type UseWorkflowDataReturn = {
  isActiveWorkflowExists: (workflow: IWorkflow) => boolean;
  showToastError: (message: string) => void;
  getWorkflowTypeLabel: (workflowTypeId: number) => string;
  canToggleActive: (workflow: IWorkflow) => boolean;
};

type UseWorkflowDataProps = {
  workflows: IWorkflow[];
  messages: WorkflowMessages;
  resolveWorkflowTypeLabel?: (workflowTypeId: number) => string;
};

function useWorkflowData({
  workflows,
  messages,
  resolveWorkflowTypeLabel,
}: UseWorkflowDataProps): UseWorkflowDataReturn {
  const { showToast } = useToast();

  const isActiveWorkflowExists = (workflow: IWorkflow) =>
    workflows.some(
      (wf) => wf.active && wf.workflowTypeId === workflow.workflowTypeId && wf.id !== workflow.id
    );

  const showToastError = (message: string) => showToast({ text: message, type: "error" });

  const getWorkflowTypeLabel = (workflowTypeId: number) => {
    const resolved = resolveWorkflowTypeLabel?.(workflowTypeId);
    if (resolved !== undefined && resolved !== "") return resolved;

    switch (workflowTypeId) {
      case WorkflowTypes.StartFlow:
        return messages.startFlow;
      case WorkflowTypes.OrderStatus:
        return messages.orderStatus;
      case WorkflowTypes.Shopping:
        return messages.shopping;
      default:
        return "-";
    }
  };

  const canToggleActive = (workflow: IWorkflow) => {
    if (!workflow.active && isActiveWorkflowExists(workflow)) {
      showToastError(messages.activeWorkflowConflictMessage);
      return false;
    }

    return true;
  };

  return {
    isActiveWorkflowExists,
    showToastError,
    getWorkflowTypeLabel,
    canToggleActive,
    ...workflows,
  };
}

export default useWorkflowData;
